import React, { MouseEvent } from 'react';
import styled, { css } from 'styled-components';
import { SvgComponent } from '../../../assets';
import {
  breakpoint,
  breakpointMap,
  BreakpointsOfTOrT,
  theme,
} from '../../../theme';
import { PrimaryButton, SecondaryButton } from '../../button';
import { ContactCardContent, ContactCardIconSize } from './ContactCardContent';

type ContactCardSizing = 'small' | 'large';

const ContactCardBaseStylesSmall = css`
  flex-direction: column;
  width: fit-content;

  > :first-child:not(:only-child) {
    margin-bottom: ${theme.spacings[5].rem};
  }
`;
const ContactCardBaseStylesLarge = css`
  flex-direction: row;
  flex-wrap: wrap;
  min-width: fit-content;

  > :first-child:not(:only-child) {
    margin-right: ${theme.spacings[5].rem};
    margin-bottom: ${theme.spacings[5].rem};
  }

  > * + * {
    flex: 2 2 60%;
  }
`;

const contactCardBaseSizes = {
  small: ContactCardBaseStylesSmall,
  large: ContactCardBaseStylesLarge,
};
const ContactCardBase = styled.article<{
  size: BreakpointsOfTOrT<ContactCardSizing>;
}>`
  display: flex;

  ${({ size }) =>
    breakpointMap(
      size,
      (cardSize) =>
        css`
          ${contactCardBaseSizes[cardSize]};
        `
    )};
`;

const ContactCardActionsStylesSmall = css`
  flex-direction: column;

  > * + * {
    margin-top: ${theme.spacings[2].rem};

    ${breakpoint('sm')`
      margin-top: ${theme.spacings[4].rem};
    `};
  }
`;

const ContactCardActionsStylesLarge = css`
  flex-direction: row;

  > * + * {
    margin-left: ${theme.spacings[2].rem};

    ${breakpoint('sm')`
      margin-left: ${theme.spacings[4].rem};
    `};
  }
`;

const contactCardActionsSizes = {
  small: ContactCardActionsStylesSmall,
  large: ContactCardActionsStylesLarge,
};

const ContactCardActions = styled.div<Pick<ContactCardProps, 'size'>>`
  display: flex;
  width: 100%;

  ${({ size }) =>
    breakpointMap(
      size,
      (cardSize) =>
        css`
          ${contactCardActionsSizes[cardSize]};
        `
    )};
`;

export type ContactCardProps = {
  secondaryActionButton?: React.ReactNode;
  primaryActionButton?: React.ReactNode;
  secondaryActionContent?: React.ReactNode;
  primaryActionContent?: React.ReactNode;
  onPrimaryClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  onSecondaryClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  icon?: SvgComponent;
  iconSize?: ContactCardIconSize;
  subtitle: string;
  title: string;
  size: BreakpointsOfTOrT<ContactCardSizing>;
  className?: string;
};

export const ContactCard: React.FC<ContactCardProps> = ({
  secondaryActionButton,
  primaryActionButton,
  secondaryActionContent,
  primaryActionContent,
  onSecondaryClick,
  onPrimaryClick,
  subtitle,
  icon,
  title,
  size = 'large',
  iconSize,
  className,
}) => (
  <ContactCardBase size={size} className={className}>
    <ContactCardContent
      title={title}
      subtitle={subtitle}
      icon={icon}
      iconSize={iconSize}
    />
    <ContactCardActions size={size}>
      {primaryActionButton ? (
        primaryActionButton
      ) : (
        <PrimaryButton size="large" fullWidth={true} onClick={onPrimaryClick}>
          {primaryActionContent}
        </PrimaryButton>
      )}
      {secondaryActionButton ? (
        secondaryActionButton
      ) : (
        <SecondaryButton
          size="large"
          fullWidth={true}
          onClick={onSecondaryClick}
        >
          {secondaryActionContent}
        </SecondaryButton>
      )}
    </ContactCardActions>
  </ContactCardBase>
);
